import React, { useRef, useEffect, useState } from "react";
import {
  motion,
  useInView,
  useScroll,
  useTransform,
  useSpring,
} from "framer-motion";
import Icon from "../../../components/AppIcon";

const BusinessInActionSection = () => {
  const [activeScene, setActiveScene] = useState(0);
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"],
  });

  // Smooth spring animations
  const springConfig = { stiffness: 100, damping: 30, restDelta: 0.001 };
  const y = useSpring(
    useTransform(scrollYProgress, [0, 1], [100, -100]),
    springConfig
  );
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]);
  const scale = useTransform(
    scrollYProgress,
    [0, 0.2, 0.8, 1],
    [0.8, 1, 1, 0.8]
  );

  const businessScenes = [
    {
      id: "inventory",
      title: "Smart Inventory Management",
      subtitle: "Never run out of stock again",
      description:
        "Real-time tracking across multiple locations with automated alerts",
      icon: "Package",
      color: "from-blue-500 to-cyan-500",
      stats: [
        { label: "Stock Level", value: "89%", trend: "stable" },
        { label: "Low Stock Alerts", value: "3", trend: "warning" },
        { label: "Reorder Points", value: "12", trend: "active" },
      ],
      elements: [
        { type: "box", x: 20, y: 30, size: 40, delay: 0 },
        { type: "box", x: 70, y: 45, size: 35, delay: 0.2 },
        { type: "scanner", x: 45, y: 20, delay: 0.4 },
        { type: "alert", x: 80, y: 25, delay: 0.6 },
      ],
    },
    {
      id: "orders",
      title: "Seamless Order Tracking",
      subtitle: "From order to delivery",
      description:
        "Track every order with real-time updates and customer notifications",
      icon: "ShoppingCart",
      color: "from-green-500 to-emerald-500",
      stats: [
        { label: "Active Orders", value: "24", trend: "up" },
        { label: "Completed Today", value: "156", trend: "up" },
        { label: "Avg. Fulfillment", value: "2.3h", trend: "down" },
      ],
      elements: [
        { type: "order", x: 15, y: 40, delay: 0 },
        { type: "truck", x: 60, y: 35, delay: 0.3 },
        { type: "customer", x: 85, y: 50, delay: 0.6 },
        { type: "checkmark", x: 40, y: 60, delay: 0.9 },
      ],
    },
    {
      id: "sales",
      title: "Real-time Sales Insights",
      subtitle: "Data-driven decisions",
      description:
        "Monitor performance across all channels with intelligent analytics",
      icon: "TrendingUp",
      color: "from-purple-500 to-pink-500",
      stats: [
        { label: "Today's Revenue", value: "$13,650", trend: "up" },
        { label: "Transactions", value: "189", trend: "up" },
        { label: "Growth Rate", value: "+23%", trend: "up" },
      ],
      elements: [
        { type: "chart", x: 30, y: 25, delay: 0 },
        { type: "phone", x: 70, y: 40, delay: 0.2 },
        { type: "notification", x: 20, y: 55, delay: 0.4 },
        { type: "graph", x: 50, y: 65, delay: 0.6 },
      ],
    },
  ];

  // Auto-rotate scenes
  useEffect(() => {
    if (!isInView) return;

    const interval = setInterval(() => {
      setActiveScene((prev) => (prev + 1) % businessScenes?.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [isInView, businessScenes?.length]);

  const currentScene = businessScenes?.[activeScene];

  return (
    <section
      id="screenshots"
      ref={sectionRef}
      className="py-20 lg:py-32 bg-gradient-to-br from-gray-50 to-white overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
          transition={{ duration: 0.6 }}
        >
          <motion.div
            className="inline-flex items-center px-4 py-2 bg-primary/10 rounded-full text-primary text-sm font-medium mb-6"
            whileHover={{ scale: 1.05 }}
            transition={{ type: "spring", stiffness: 300 }}
          >
            <Icon name="Zap" size={16} className="mr-2" />
            Business in Action
          </motion.div>
          <motion.h2
            className="text-3xl lg:text-5xl font-bold text-text-primary mb-6"
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            See Your Business Transform
          </motion.h2>
          <motion.p
            className="text-lg text-text-secondary max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            Watch how our app simplifies complex business operations with
            intelligent automation, real-time insights, and seamless workflow
            integration.
          </motion.p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Interactive 3D Business Environment */}
          <motion.div
            className="relative order-2 lg:order-1"
            style={{ opacity, scale }}
          >
            <div className="relative h-96 lg:h-[500px] bg-white rounded-3xl shadow-2xl overflow-hidden">
              {/* Dynamic Background Gradient */}
              <motion.div
                className={`absolute inset-0 bg-gradient-to-br ${currentScene?.color} opacity-10`}
                key={currentScene?.id}
                initial={{ opacity: 0, scale: 1.2 }}
                animate={{ opacity: 0.1, scale: 1 }}
                transition={{ duration: 1 }}
              />

              {/* 3D Business Environment */}
              <div className="absolute inset-0 p-8">
                <motion.div className="relative h-full w-full" style={{ y }}>
                  {/* Animated Business Elements */}
                  {currentScene?.elements?.map((element, index) => (
                    <motion.div
                      key={`${currentScene?.id}-${index}`}
                      className="absolute"
                      style={{
                        left: `${element?.x}%`,
                        top: `${element?.y}%`,
                      }}
                      initial={{
                        opacity: 0,
                        scale: 0,
                        rotate: -180,
                        y: 50,
                      }}
                      animate={
                        isInView
                          ? {
                              opacity: 1,
                              scale: 1,
                              rotate: 0,
                              y: 0,
                            }
                          : {}
                      }
                      transition={{
                        duration: 0.8,
                        delay: element?.delay,
                        type: "spring",
                        stiffness: 100,
                      }}
                      whileHover={{
                        scale: 1.2,
                        rotate: 10,
                        transition: { duration: 0.2 },
                      }}
                    >
                      {element?.type === "box" && (
                        <div
                          className={`w-${
                            element?.size === 40
                              ? "12"
                              : element?.size === 35
                              ? "10"
                              : "8"
                          } h-${
                            element?.size === 40
                              ? "12"
                              : element?.size === 35
                              ? "10"
                              : "8"
                          } bg-gradient-to-br from-blue-400 to-blue-600 rounded-lg shadow-lg flex items-center justify-center`}
                        >
                          <Icon
                            name="Package"
                            size={element?.size === 40 ? 24 : 20}
                            className="text-white"
                          />
                        </div>
                      )}
                      {element?.type === "scanner" && (
                        <div className="w-8 h-8 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full shadow-lg flex items-center justify-center">
                          <Icon name="Scan" size={16} className="text-white" />
                        </div>
                      )}
                      {element?.type === "alert" && (
                        <motion.div
                          className="w-6 h-6 bg-gradient-to-br from-red-400 to-red-600 rounded-full shadow-lg flex items-center justify-center"
                          animate={{ scale: [1, 1.2, 1] }}
                          transition={{ duration: 2, repeat: Infinity }}
                        >
                          <Icon
                            name="AlertTriangle"
                            size={12}
                            className="text-white"
                          />
                        </motion.div>
                      )}
                      {element?.type === "order" && (
                        <div className="w-10 h-10 bg-gradient-to-br from-green-400 to-emerald-500 rounded-xl shadow-lg flex items-center justify-center">
                          <Icon
                            name="FileText"
                            size={20}
                            className="text-white"
                          />
                        </div>
                      )}
                      {element?.type === "truck" && (
                        <motion.div
                          className="w-12 h-8 bg-gradient-to-br from-gray-600 to-gray-800 rounded-lg shadow-lg flex items-center justify-center"
                          animate={{ x: [0, 10, 0] }}
                          transition={{ duration: 3, repeat: Infinity }}
                        >
                          <Icon name="Truck" size={20} className="text-white" />
                        </motion.div>
                      )}
                      {element?.type === "customer" && (
                        <div className="w-8 h-8 bg-gradient-to-br from-indigo-400 to-purple-500 rounded-full shadow-lg flex items-center justify-center">
                          <Icon name="User" size={16} className="text-white" />
                        </div>
                      )}
                      {element?.type === "checkmark" && (
                        <motion.div
                          className="w-8 h-8 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full shadow-lg flex items-center justify-center"
                          initial={{ scale: 0, rotate: -180 }}
                          animate={{ scale: 1, rotate: 0 }}
                          transition={{
                            delay: element?.delay + 0.5,
                            type: "spring",
                            stiffness: 200,
                          }}
                        >
                          <Icon name="Check" size={16} className="text-white" />
                        </motion.div>
                      )}
                      {element?.type === "chart" && (
                        <div className="w-16 h-12 bg-gradient-to-br from-purple-400 to-pink-500 rounded-lg shadow-lg flex items-center justify-center">
                          <Icon
                            name="BarChart3"
                            size={20}
                            className="text-white"
                          />
                        </div>
                      )}
                      {element?.type === "phone" && (
                        <motion.div
                          className="w-6 h-10 bg-gradient-to-br from-gray-800 to-black rounded-lg shadow-lg flex items-center justify-center"
                          animate={{ rotate: [0, 5, -5, 0] }}
                          transition={{ duration: 4, repeat: Infinity }}
                        >
                          <Icon
                            name="Smartphone"
                            size={16}
                            className="text-white"
                          />
                        </motion.div>
                      )}
                      {element?.type === "notification" && (
                        <motion.div
                          className="w-8 h-6 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-md shadow-lg flex items-center justify-center"
                          animate={{ opacity: [0.7, 1, 0.7] }}
                          transition={{ duration: 2, repeat: Infinity }}
                        >
                          <Icon name="Bell" size={12} className="text-white" />
                        </motion.div>
                      )}
                      {element?.type === "graph" && (
                        <motion.div
                          className="w-12 h-8 bg-gradient-to-br from-teal-400 to-cyan-500 rounded-lg shadow-lg flex items-center justify-center"
                          animate={{ scaleY: [1, 1.2, 1] }}
                          transition={{
                            duration: 2,
                            repeat: Infinity,
                            delay: element?.delay,
                          }}
                        >
                          <Icon
                            name="TrendingUp"
                            size={16}
                            className="text-white"
                          />
                        </motion.div>
                      )}
                    </motion.div>
                  ))}

                  {/* Floating Stats */}
                  <div className="absolute top-4 right-4 space-y-2">
                    {currentScene?.stats?.map((stat, index) => (
                      <motion.div
                        key={`stat-${currentScene?.id}-${index}`}
                        className="bg-white/90 backdrop-blur-sm rounded-lg p-2 shadow-lg min-w-24"
                        initial={{ opacity: 0, x: 30 }}
                        animate={isInView ? { opacity: 1, x: 0 } : {}}
                        transition={{ delay: 0.5 + index * 0.1 }}
                        whileHover={{ scale: 1.05 }}
                      >
                        <div className="text-xs text-gray-600">
                          {stat?.label}
                        </div>
                        <div className="text-sm font-bold text-gray-900">
                          {stat?.value}
                        </div>
                        <div
                          className={`text-xs flex items-center ${
                            stat?.trend === "up"
                              ? "text-green-500"
                              : stat?.trend === "down"
                              ? "text-red-500"
                              : stat?.trend === "warning"
                              ? "text-yellow-500"
                              : "text-gray-500"
                          }`}
                        >
                          {stat?.trend === "up" && (
                            <Icon
                              name="TrendingUp"
                              size={10}
                              className="mr-1"
                            />
                          )}
                          {stat?.trend === "down" && (
                            <Icon
                              name="TrendingDown"
                              size={10}
                              className="mr-1"
                            />
                          )}
                          {stat?.trend === "warning" && (
                            <Icon
                              name="AlertTriangle"
                              size={10}
                              className="mr-1"
                            />
                          )}
                          {stat?.trend === "active" && (
                            <Icon name="Activity" size={10} className="mr-1" />
                          )}
                          {stat?.trend === "stable" && (
                            <Icon name="Minus" size={10} className="mr-1" />
                          )}
                          {stat?.trend}
                        </div>
                      </motion.div>
                    ))}
                  </div>

                  {/* Connecting Lines Animation */}
                  <svg className="absolute inset-0 w-full h-full pointer-events-none">
                    <motion.path
                      d="M 20 80 Q 50 60 80 70"
                      stroke="url(#gradient)"
                      strokeWidth="2"
                      fill="none"
                      strokeDasharray="5,5"
                      initial={{ pathLength: 0, opacity: 0 }}
                      animate={isInView ? { pathLength: 1, opacity: 0.6 } : {}}
                      transition={{ duration: 2, delay: 1 }}
                    />
                    <defs>
                      <linearGradient
                        id="gradient"
                        x1="0%"
                        y1="0%"
                        x2="100%"
                        y2="0%"
                      >
                        <stop
                          offset="0%"
                          stopColor="#3B82F6"
                          stopOpacity="0.8"
                        />
                        <stop
                          offset="100%"
                          stopColor="#8B5CF6"
                          stopOpacity="0.8"
                        />
                      </linearGradient>
                    </defs>
                  </svg>
                </motion.div>
              </div>

              {/* Scene Progress Indicator */}
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
                {businessScenes?.map((_, index) => (
                  <motion.button
                    key={index}
                    className={`w-2 h-2 rounded-full transition-colors ${
                      index === activeScene ? "bg-primary" : "bg-gray-300"
                    }`}
                    onClick={() => setActiveScene(index)}
                    whileHover={{ scale: 1.5 }}
                    whileTap={{ scale: 0.8 }}
                  />
                ))}
              </div>
            </div>
          </motion.div>

          {/* Content Panel */}
          <motion.div
            className="order-1 lg:order-2"
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: 50 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="space-y-8">
              {/* Scene Navigation */}
              <div className="grid grid-cols-3 gap-4">
                {businessScenes?.map((scene, index) => (
                  <motion.button
                    key={scene?.id}
                    className={`p-4 rounded-xl text-left transition-all ${
                      activeScene === index
                        ? "bg-primary text-white shadow-lg"
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                    onClick={() => setActiveScene(index)}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Icon name={scene?.icon} size={24} className="mb-2" />
                    <div className="text-sm font-semibold">
                      {scene?.title?.split(" ")?.[0]}
                    </div>
                    <div className="text-xs opacity-75">
                      {scene?.title?.split(" ")?.slice(1)?.join(" ")}
                    </div>
                  </motion.button>
                ))}
              </div>

              {/* Active Scene Details */}
              <motion.div
                key={currentScene?.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="space-y-6"
              >
                <div>
                  <div className="flex items-center mb-2">
                    <div
                      className={`w-12 h-12 rounded-xl bg-gradient-to-br ${currentScene?.color} flex items-center justify-center mr-4`}
                    >
                      <Icon
                        name={currentScene?.icon}
                        size={24}
                        className="text-white"
                      />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-text-primary">
                        {currentScene?.title}
                      </h3>
                      <p className="text-primary font-medium">
                        {currentScene?.subtitle}
                      </p>
                    </div>
                  </div>
                  <p className="text-text-secondary text-lg leading-relaxed">
                    {currentScene?.description}
                  </p>
                </div>

                {/* Benefits List */}
                <div className="space-y-3">
                  <h4 className="font-semibold text-text-primary">
                    Key Benefits:
                  </h4>
                  {[
                    "Automated workflow management",
                    "Real-time data synchronization",
                    "Multi-location coordination",
                    "Intelligent alerts and notifications",
                  ]?.map((benefit, index) => (
                    <motion.div
                      key={index}
                      className="flex items-center"
                      initial={{ opacity: 0, x: -20 }}
                      animate={isInView ? { opacity: 1, x: 0 } : {}}
                      transition={{ delay: 0.8 + index * 0.1 }}
                    >
                      <Icon
                        name="CheckCircle2"
                        size={16}
                        className="text-success mr-3 flex-shrink-0"
                      />
                      <span className="text-text-secondary">{benefit}</span>
                    </motion.div>
                  ))}
                </div>

                {/* Download CTA */}
                <motion.div
                  className="pt-6"
                  initial={{ opacity: 0, y: 20 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: 1.2 }}
                >
                  <motion.button
                    className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-primary to-primary/80 text-white rounded-xl font-semibold text-lg shadow-lg hover:shadow-xl transition-all"
                    whileHover={{
                      scale: 1.05,
                      boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
                    }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => {
                      // Analytics tracking
                      if (window.gtag) {
                        window.gtag("event", "download_app_click", {
                          event_category: "CTA",
                          event_label: "Business in Action Section",
                          value: activeScene,
                        });
                      }
                    }}
                  >
                    <Icon name="Download" size={20} className="mr-2" />
                    Download the App
                    <motion.div
                      className="ml-2"
                      animate={{ x: [0, 5, 0] }}
                      transition={{ duration: 1.5, repeat: Infinity }}
                    >
                      <Icon name="ArrowRight" size={20} />
                    </motion.div>
                  </motion.button>
                  <p className="text-sm text-text-secondary mt-3">
                    Available on iOS and Android • Free to trial
                  </p>
                </motion.div>
              </motion.div>
            </div>
          </motion.div>
        </div>

        {/* Bottom Stats */}
        <motion.div
          className="grid grid-cols-2 lg:grid-cols-4 gap-6 mt-16 pt-16 border-t border-gray-200"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 1.5 }}
        >
          {[
            { label: "Time Saved Daily", value: "4.2 hrs", icon: "Clock" },
            { label: "Error Reduction", value: "87%", icon: "ShieldCheck" },
            { label: "Revenue Increase", value: "+34%", icon: "TrendingUp" },
            { label: "Customer Satisfaction", value: "96%", icon: "Heart" },
          ]?.map((stat, index) => (
            <motion.div
              key={index}
              className="text-center p-6 rounded-xl bg-white shadow-sm border border-gray-100"
              whileHover={{
                y: -5,
                boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.1)",
              }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <Icon
                name={stat?.icon}
                size={32}
                className="text-primary mx-auto mb-3"
              />
              <div className="text-2xl font-bold text-text-primary mb-1">
                {stat?.value}
              </div>
              <div className="text-sm text-text-secondary">{stat?.label}</div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default BusinessInActionSection;
